import React from 'react';
import { Box, Card, CardContent, Typography, Button, Alert, Collapse } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null, 
            errorInfo: null,
            showDetails: false 
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('ErrorBoundary caught a runtime error:', error, errorInfo);
        this.setState({ errorInfo });

        // Auto-heal chunk load error (when new frontend build is deployed and old chunk 404s)
        const errMsg = error?.message || '';
        if (
            errMsg.includes('QuotaExceeded') ||
            errMsg.includes('exceeded the quota') ||
            error?.name === 'QuotaExceededError'
        ) {
            console.warn('Auto-recovering from QuotaExceededError by clearing old cached orders...');
            try {
                const keysToRemove = [];
                for (let i = 0; i < localStorage.length; i++) {
                    const k = localStorage.key(i);
                    if (k && k.includes('react_pos_orders')) {
                        keysToRemove.push(k);
                    }
                }
                keysToRemove.forEach(k => localStorage.removeItem(k));
            } catch (e) {}
            window.location.reload();
            return;
        }

        if (
            errMsg.includes('Loading chunk') || 
            errMsg.includes('ChunkLoadError') || 
            errMsg.includes('Unexpected token <') ||
            errMsg.includes('Failed to fetch dynamically imported module')
        ) {
            console.warn('Auto-recovering from chunk load error by reloading with fresh cache...');
            const lastReload = sessionStorage.getItem('dinexa_last_chunk_reload');
            const now = Date.now();
            if (!lastReload || now - parseInt(lastReload) > 10000) {
                sessionStorage.setItem('dinexa_last_chunk_reload', now.toString());
                if ('caches' in window) {
                    caches.keys().then(names => Promise.all(names.map(name => caches.delete(name)))).finally(() => {
                        window.location.reload(true);
                    });
                } else {
                    window.location.reload(true);
                }
            }
        }
    }

    handleHardReload = () => {
        if ('caches' in window) {
            caches.keys().then(names => Promise.all(names.map(name => caches.delete(name)))).finally(() => {
                window.location.reload(true);
            });
        } else {
            window.location.reload(true);
        }
    };

    handleClearCacheAndReset = () => {
        try {
            // Keep critical credentials but clear potentially corrupted data caches
            const keepKeys = ['dinexa_active_tenant_subdomain', 'react_pos_current_user', 'dinexa_is_logged_in', 'react_pos_current_role'];
            const savedItems = {};
            keepKeys.forEach(k => {
                const val = localStorage.getItem(k) || sessionStorage.getItem(k);
                if (val) savedItems[k] = val;
            });

            localStorage.clear();
            sessionStorage.clear();

            Object.entries(savedItems).forEach(([k, v]) => {
                localStorage.setItem(k, v);
                sessionStorage.setItem(k, v);
            });

            if ('caches' in window) {
                caches.keys().then(names => Promise.all(names.map(name => caches.delete(name))));
            }
        } catch (e) {
            console.error('Error clearing cache:', e);
        }
        window.location.reload(true);
    };

    render() {
        if (this.state.hasError) {
            return (
                <Box sx={{
                    minHeight: '100vh',
                    bgcolor: '#0f172a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 3
                }}>
                    <Card sx={{
                        maxWidth: 550,
                        width: '100%',
                        borderRadius: 4,
                        bgcolor: '#1e293b',
                        color: '#f8fafc',
                        border: '1px solid #334155',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                    }}>
                        <CardContent sx={{ p: 4, textAlign: 'center' }}>
                            <Box sx={{
                                width: 72,
                                height: 72,
                                bgcolor: '#fee2e2',
                                color: '#dc2626',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2.5
                            }}>
                                <WarningAmberIcon sx={{ fontSize: 44 }} />
                            </Box>

                            <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, color: '#f8fafc' }}>
                                स्क्रीन लोड करताना अडचण आली
                            </Typography>
                            <Typography variant="subtitle2" sx={{ color: '#94a3b8', mb: 3, fontWeight: 600 }}>
                                (An unexpected error occurred in UI rendering)
                            </Typography>

                            <Alert severity="info" sx={{ mb: 3, textAlign: 'left', borderRadius: 2, bgcolor: '#0284c720', color: '#38bdf8', border: '1px solid #0284c740' }}>
                                नवीन अपडेट किंवा जुन्या ब्राऊझर कॅशमुळे हे होऊ शकते. खालील <strong>"रीफ्रेश करा"</strong> बटणावर क्लिक करा.
                            </Alert>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<RefreshIcon />}
                                    onClick={this.handleHardReload}
                                    sx={{
                                        bgcolor: '#008751',
                                        '&:hover': { bgcolor: '#006f42' },
                                        fontWeight: 800,
                                        py: 1.3,
                                        borderRadius: 2.5,
                                        textTransform: 'none',
                                        fontSize: '1rem'
                                    }}
                                >
                                    🔄 ॲप रिफ्रेश करा (Refresh App)
                                </Button>

                                <Button
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<DeleteSweepIcon />}
                                    onClick={this.handleClearCacheAndReset}
                                    sx={{
                                        fontWeight: 800,
                                        py: 1.1,
                                        borderRadius: 2.5,
                                        textTransform: 'none',
                                        borderColor: '#f59e0b',
                                        color: '#f59e0b',
                                        '&:hover': { bgcolor: '#f59e0b15', borderColor: '#d97706' }
                                    }}
                                >
                                    🧹 कॅश साफ करा व फ्रेश सुरू करा (Clear Cache & Recover)
                                </Button>
                            </Box>

                            <Button
                                size="small"
                                variant="text"
                                onClick={() => this.setState(prev => ({ showDetails: !prev.showDetails }))}
                                sx={{ color: '#64748b', fontSize: '0.8rem', textTransform: 'none' }}
                            >
                                {this.state.showDetails ? 'तांत्रिक तपशील लपवा (Hide Details)' : 'तांत्रिक तपशील पहा (Technical Error Details)'}
                            </Button>

                            <Collapse in={this.state.showDetails}>
                                <Box sx={{
                                    mt: 2,
                                    p: 2,
                                    bgcolor: '#020617',
                                    borderRadius: 2,
                                    textAlign: 'left',
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                    color: '#f87171',
                                    maxHeight: 180,
                                    overflowY: 'auto',
                                    wordBreak: 'break-word',
                                    border: '1px solid #1e293b'
                                }}>
                                    <div><strong>Error:</strong> {this.state.error?.toString()}</div>
                                    <div style={{ marginTop: 8, color: '#94a3b8' }}>
                                        {this.state.errorInfo?.componentStack}
                                    </div>
                                </Box>
                            </Collapse>
                        </CardContent>
                    </Card>
                </Box>
            );
        }

        return this.props.children;
    }
}
